import {
  BREAKPOINTS,
  SIZING_2,
  SIZING_3,
  SPACING_0_5,
  SPACING_2,
  WEB_PURPLE_REG,
  WEB_WHITE_REG,
} from '@mnd-frontend/ui/shared/tokens';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { formatUrl } from '../../utils/formatters/url';

export const textColorTheme = ['dark', 'light'] as const;

export type TextColorThemeUnion = (typeof textColorTheme)[number];

type BreadcrumbItemType = {
  text: string;
  url: string;
};

type BreadcrumbType = Array<BreadcrumbItemType>;

const BreadcrumbNav = styled.nav`
  position: relative;
  z-index: 10;
`;
const OrderedList = styled.ol<{ $textColorTheme: TextColorThemeUnion }>`
  position: absolute;
  text-overflow: ellipsis;
  top: 0.75rem;
  left: ${SPACING_2};
  font-size: 0.875rem;
  margin: 0;
  padding: 0;
  list-style: none;
  color: ${({ $textColorTheme }) => ($textColorTheme === 'dark' ? WEB_PURPLE_REG : WEB_WHITE_REG)};

  @media (min-width: ${BREAKPOINTS.XL}px) {
    left: 0;
    font-size: ${SIZING_2};
  }
`;

const ListItem = styled.li`
  display: inline-block;
  color: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 9rem;
  height: ${SIZING_3};

  @media (min-width: ${BREAKPOINTS.LG}px) {
    max-width: 20rem;
  }

  &:not(:first-of-type)::before {
    content: "/";
    margin-left: ${SPACING_0_5};
    margin-right: ${SPACING_0_5};
  }
`;

const ItemTextBase = css`
 // Margin for focus outline.
  margin: 0 ${SPACING_0_5} 0 1px;

  &:not(:first-of-type) {
    margin-left: ${SPACING_0_5};
  }
`;

const ItemTextLink = styled(Link)`
  ${ItemTextBase}

  &:focus-visible {
    outline-offset: 0px;
  }
`;

const ItemTextPlain = styled.span`
  ${ItemTextBase}
`;

const Breadcrumb = ({
  breadcrumb,
  textColorTheme = 'dark',
}: {
  breadcrumb: BreadcrumbType;
  textColorTheme?: TextColorThemeUnion;
}) => {
  const { t } = useTranslation();

  if (!breadcrumb) return null;

  return (
    <BreadcrumbNav aria-label={t('common_breadcrumb', 'breadcrumb')}>
      <OrderedList $textColorTheme={textColorTheme}>
        {breadcrumb.map((item, idx) => (
          <ListItem key={item.text.split(' ').join('')}>
            {breadcrumb.length - 1 === idx ? (
              <ItemTextPlain aria-current="page">{item.text}</ItemTextPlain>
            ) : (
              <ItemTextLink
                href={formatUrl(item.url)}
                // Handling encoded symbols such as &#038;
                dangerouslySetInnerHTML={{
                  __html: item.text || '',
                }}
                prefetch={false}
              />
            )}
          </ListItem>
        ))}
      </OrderedList>
    </BreadcrumbNav>
  );
};

export default Breadcrumb;
